import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getPlanType } from "./utils";
import { resolveFilterOption } from "../merchandising/components/tables/resolveFilterOption";

const PLAN_TYPE_OPTIONS = [
  {
    value: "same_strategy",
    label: ($) => $.pricingv2["Dynamic Price"],
  },
  {
    value: "rules_engine",
    label: ($) => $.pricingv2["Rules Engine"],
  },
];

const STRATEGY_OPTIONS = [
  { value: "volume", label: ($) => $.pricingv2.strategy["Volume"] },
  { value: "margin", label: ($) => $.pricingv2.strategy["Margin"] },
  { value: "balanced", label: ($) => $.pricingv2.strategy["Balanced"] },
];

const RULES_BADGE_OPTIONS = [
  { value: "CLEARANCE", label: ($) => $.pricingv2["Clearance"] },
  { value: "MARGIN_PROFILING", label: ($) => $.pricingv2["Margin Profiling"] },
  { value: "BSR", label: ($) => $.pricingv2["BSR"] },
  { value: "CUSTOM", label: ($) => $.pricingv2["Custom"] },
];

const SEGMENT_OPTIONS = [
  { value: "sales_pressure", label: ($) => $.pricingv2["Sales Pressure"] },
  { value: "profit_potential", label: ($) => $.pricingv2["Profit Potential"] },
  { value: "right_price", label: ($) => $.pricingv2["Right Price"] },
  { value: "out_of_stock", label: ($) => $.pricingv2["Out of Stock"] },
  { value: "slow_movers", label: ($) => $.pricingv2["Slow Movers"] },
  { value: "new_plan", label: ($) => $.pricingv2["Profiling"] },
];

export const PLAN_SEGMENT_FILTER = {
  name: ($) => $.pricingv2["Segment"],
  id: "segment",
  format: "select",
  selectOptions: [
    {
      label: ($) => $.pricingv2["All Segments"],
      value: "all",
      clearable: false,
    },
    ...SEGMENT_OPTIONS,
  ],
  override: true,
  defaultValue: "all",
  operator: "=",
  filterFunction: (plan, filterValue) => {
    if (filterValue !== "all") {
      if (filterValue === "slow_movers") {
        return ["no_price", "slow_movers"].includes(plan.bucket);
      }
      return plan.bucket === filterValue;
    }
    return true;
  },
};

export const RULES_FILTER = {
  name: ($) => $.pricingv2["Pricing Rules"],
  id: "pricing_rules",
  format: "select",
  selectOptions: [
    {
      label: ($) => $.pricingv2["All Rules"],
      value: "all",
      clearable: false,
    },
    ...RULES_BADGE_OPTIONS,
  ],
  override: true,
  defaultValue: "all",
  operator: "=",
  filterFunction: (plan, filterValue) => {
    if (filterValue !== "all") {
      const type = getPlanType(plan);
      if (type !== "Rules Engine") {
        return false;
      }

      if (filterValue === "CUSTOM") {
        return plan.current_program__template_type === null;
      }

      if (filterValue === "MARGIN_PROFILING") {
        return ["MARGIN_PROFILING", "PROFILE"].includes(plan.current_program__template_type);
      }
      return plan.current_program__template_type === filterValue;
    }
    return true;
  },
};

export const PLAN_TYPE_FILTER = {
  name: ($) => $.pricingv2["Plan Type"],
  id: "plan_type",
  format: "select",
  selectOptions: [
    {
      label: ($) => $.pricingv2["All Plan Types"],
      value: "all",
      clearable: false,
    },
    ...PLAN_TYPE_OPTIONS,
  ],
  override: true,
  defaultValue: "all",
  operator: "=",
  filterFunction: (plan, filterValue) => {
    if (filterValue !== "all") {
      const type = getPlanType(plan);

      if (["same_strategy", "same_type"].includes(filterValue) && type === "Dynamic Price") {
        return true;
      }

      if (filterValue === "rules_engine" && type === "Rules Engine") {
        return true;
      }

      return false;
    }

    return true;
  },
};

export const PLAN_STRATEGY_FILTER = {
  name: ($) => $.pricingv2["Strategy"],
  id: "strategy",
  format: "select",
  selectOptions: [
    {
      label: ($) => $.pricingv2["Strategy"],
      value: "all",
      clearable: false,
    },
    ...STRATEGY_OPTIONS,
  ],
  override: true,
  defaultValue: "all",
  operator: "=",
  filterFunction: (plan, filterValue) => {
    if (filterValue !== "all") {
      if (filterValue === "volume") {
        return plan.configured_slider_value < 0;
      } else if (filterValue === "margin") {
        return plan.configured_slider_value > 0;
      } else if (filterValue === "balanced") {
        return plan.configured_slider_value === 0;
      }
    }
    return true;
  },
};

// Resolves the pricingv2-namespace selectors carried by the filter defs above
// into plain strings, bound to the `pricingv2` namespace at the call site so
// the generic FilterWidget never has to resolve them. See resolveFilterOption.
export function usePricingLifecycleFilters() {
  const { t } = useTranslation(["pricingv2"]);
  return useMemo(
    () => ({
      PLAN_SEGMENT_FILTER: resolveFilterOption(t, PLAN_SEGMENT_FILTER),
      RULES_FILTER: resolveFilterOption(t, RULES_FILTER),
      PLAN_TYPE_FILTER: resolveFilterOption(t, PLAN_TYPE_FILTER),
      PLAN_STRATEGY_FILTER: resolveFilterOption(t, PLAN_STRATEGY_FILTER),
    }),
    [t],
  );
}
